import { FilterTagType } from '@globalmed/atlas-ui';
import dayjs from 'dayjs';
import {
  IChart,
  IReportDataServer,
  IReportFilterParams,
  ITableChartData,
  TReportConfigCustom,
} from '@/types/reports';
import { getDateOnlyISOFormat, getShortDateFormat } from '@/utils/convertDate';
import { saveBlob } from '@/utils/downloadFile';

/**
 * Type guard to check if a given report data object matches the specified chart type.
 * T - Expected type of the report data.
 * @param chart - Chart configuration object.
 * @param data - Report data object to check.
 * @param type - Expected chart type to validate against.
 * @returns `true` if the data matches the specified chart type, otherwise `false`.
 */
export function isChartDataType<T extends IReportDataServer>(
  chart: IChart,
  data: IReportDataServer,
  type: IChart['type'],
): data is T {
  if (!data) return false;
  return chart.type === type;
}

/**
 * Builds URL search parameters from filter parameters object
 * Converts filter parameters to URL format for API requests and URL synchronization.
 * All filter definitions and parameter structures come from the server configuration.
 * @param filters - Filter parameters object
 * @returns URL search parameters with filter data
 */
export const buildFilterParams = (
  filters: IReportFilterParams,
): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    const filterKey = `filter[${key}]`;
    if (Array.isArray(value)) {
      return value.forEach((val) => params.append(filterKey, String(val)));
    }
    if (typeof value === 'object' && value !== null) {
      return Object.entries(value).forEach(([subKey, subValue]) => {
        params.append(`${filterKey}[${subKey}]`, String(subValue));
      });
    }
    return params.append(filterKey, String(value));
  });

  return params;
};

/**
 * Parses filter parameters from URL search parameters
 * Converts URL parameters back to filter tags for UI display.
 * All filter configurations and options come from the server.
 * @param searchParams - URL search parameters
 * @param [chartConfig] - Chart configuration from server
 * @returns Array of filter tags for UI display
 */
export const parseFiltersFromUrl = (
  searchParams: URLSearchParams,
  chartConfig?: TReportConfigCustom,
): FilterTagType[] => {
  const tags: FilterTagType[] = [];
  const filterKeys = new Set<string>();
  for (const [key] of searchParams.entries()) {
    const match = /^filter\[([^\]]*)]/.exec(key);
    if (match) filterKeys.add(match[1]);
  }

  for (const key of filterKeys) {
    const filterConfig = chartConfig?.filters?.find((f) => f.key === key);

    const startDate = searchParams.get(`filter[${key}][start]`);
    const endDate = searchParams.get(`filter[${key}][end]`);
    if (startDate && endDate) {
      const startFormatted = getDateOnlyISOFormat(dayjs.utc(startDate));
      const endFormatted = getDateOnlyISOFormat(dayjs.utc(endDate));
      tags.push({
        key,
        type: 'dateRange',
        dateRangeValue: [startFormatted, endFormatted],
        title: `${getShortDateFormat(dayjs(startFormatted))} - ${getShortDateFormat(dayjs(endFormatted))}`,
      });
      continue;
    }

    const values = searchParams.getAll(`filter[${key}]`);
    for (const value of values) {
      const option = filterConfig?.options?.find((opt) => opt.id === value);
      tags.push({
        key,
        type: filterConfig?.type ?? 'options',
        optionValue: value,
        title: option?.title ?? value,
      });
    }
  }

  return tags;
};

/**
 * Creates filter parameters object from filter tags
 * Converts UI filter tags to parameter object for API requests.
 * All filter types and parameter structures are defined by the server configuration.
 * @param tags - Array of filter tags from UI
 * @returns Filter parameters object for API requests
 */
export const createFilterParams = (
  tags: FilterTagType[],
): IReportFilterParams => {
  const filterParams: IReportFilterParams = {};

  for (const tag of tags) {
    switch (tag.type) {
      case 'options':
      case 'select': {
        if (!tag.optionValue) continue;
        const key = filterParams[tag.key];
        if (Array.isArray(key) && key.every((v) => typeof v === 'string')) {
          if (!key.includes(tag.optionValue)) {
            filterParams[tag.key] = [...key, tag.optionValue];
          }
        } else {
          filterParams[tag.key] = [tag.optionValue];
        }
        break;
      }
      case 'dateRange': {
        if (!tag.dateRangeValue) continue;
        const [startDate, endDate] = tag.dateRangeValue;

        const startDayjs = dayjs.utc(startDate, 'YYYY-MM-DD');
        const endDayjs = dayjs.utc(endDate, 'YYYY-MM-DD');

        filterParams[tag.key] = {
          start: startDayjs.isValid()
            ? `${getDateOnlyISOFormat(startDayjs)}T00:00:00.000Z`
            : startDate,
          end: endDayjs.isValid()
            ? `${getDateOnlyISOFormat(endDayjs)}T23:59:59.999Z`
            : endDate,
        };
        break;
      }
    }
  }

  return filterParams;
};

/**
 * Prepares filter data for PDF export.
 * Converts filter tags into formatted data suitable for PDF display.
 * All filter configurations and formatting come from the server.
 *
 * Date handling specifics:
 * - The server provides the date range as a single value (`dateRangeValue`).
 * - When preparing data for the PDF, this range is split into two separate fields —
 *   "start-date" and "end-date" — so that both dates appear in different rows in the PDF.
 * @param tags - Array of filter tags.
 * @param t - Translation function.
 * @param [parentConfig] - Parent chart configuration from the server.
 * @returns Array of formatted filter data for the PDF.
 */
export const prepareFilterDataForPDF = (
  tags: FilterTagType[],
  t: (key: string) => string,
  parentConfig?: TReportConfigCustom,
) => {
  const result: { title: string; value: string }[] = [];
  if (!parentConfig?.filters) return result;
  const parentFilterKeys = new Set(
    parentConfig.filters.map((filter) => filter.key),
  );

  for (const tag of tags) {
    if (!parentFilterKeys.has(tag.key)) {
      continue;
    }

    const filterConfig = parentConfig.filters.find(
      (filter) => filter.key === tag.key,
    );
    const filterTitle = filterConfig?.name ?? tag.key;

    if (tag.dateRangeValue) {
      const startDate = dayjs(tag.dateRangeValue[0]);
      const endDate = dayjs(tag.dateRangeValue[1]);

      result.push(
        {
          title: t('start-date'),
          value: startDate.isValid()
            ? getShortDateFormat(startDate)
            : tag.dateRangeValue[0],
        },
        {
          title: t('end-date'),
          value: endDate.isValid()
            ? getShortDateFormat(endDate)
            : tag.dateRangeValue[1],
        },
      );
    } else {
      const option = filterConfig?.options?.find(
        (opt) => opt.id === tag.optionValue,
      );
      result.push({
        title: filterTitle,
        value: option?.title ?? tag.title ?? '',
      });
    }
  }

  return result;
};

/**
 * Generates CSV content from table chart data
 * Converts table data to CSV format with proper escaping and formatting.
 * All table structure and column definitions come from the server configuration.
 * @param data - Table data from server
 * @param chart - Chart configuration from server
 * @returns CSV content string
 */
export const generateCSV = (
  data: ITableChartData[][],
  chart: IChart,
): string => {
  if (!data?.length || !chart.columns?.length) {
    return '';
  }

  const headers = chart.columns.map((col) => col.name).join(',');

  const rows = data.map((row) => {
    return row
      .map((cell) => {
        const value = cell.value?.toString() ?? '';
        if (
          value.includes(',') ||
          value.includes('"') ||
          value.includes('\n')
        ) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })
      .join(',');
  });

  return [headers, ...rows].join('\n');
};

/**
 * Downloads CSV content as a file
 * Creates a blob from CSV content and triggers a download.
 * Handles proper MIME type and filename for CSV files.
 * Adds UTF-8 BOM to ensure Excel correctly interprets special characters
 * in Spanish and Portuguese (á, é, í, ó, ú, ñ, ç, etc.)
 * @param csvContent - CSV content string
 * @param filename - Filename for the downloaded file
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], {
    type: 'text/csv;charset=utf-8;',
  });

  void saveBlob(blob, filename, 'text/csv;charset=utf-8;');
};

/**
 * Generates a filename for CSV export with timestamp
 * Creates a unique filename for CSV exports with current timestamp.
 * Uses chart name or report ID as base name.
 * @param [chartName] - Chart name from server configuration
 * @param [reportId] - Report ID from server
 * @returns Generated filename with timestamp
 */
export const generateCSVFilename = (
  chartName?: string,
  reportId?: string,
): string => {
  const timestamp = dayjs().format('YYYYMMDDHHmmss');
  const baseName = chartName ?? reportId ?? 'table';
  return `${baseName}_${timestamp}.csv`;
};
